import {HeaderSpecification} from "./classes/HeaderSpecification";
import type {BagAdvancedInsightsManager} from "../../stores/managers/BagAdvancedInsightsManager";
import type ProfileStore from "../../stores/ProfileStore";
import type {SynergyStore} from "../../stores/SynergyStore";
import type {AdvancedSupplierBreakdownData, ExcelDataColumns} from "../../services/ApiTypes";
import React from "react";
import {OpportunityRankPopoverText} from "../../help/OpportunityRankPopoverText";
import {ContextMenuAction} from "./collumns/ContextMenuAction";

export function OpportunityHeaders(i: BagAdvancedInsightsManager, showL4: boolean) {
    return ([
        new HeaderSpecification(i, 'L1', 'active_l1', 'category', 'opportunity_'),
        new HeaderSpecification(i, 'L2', 'active_l2', 'category', 'opportunity_'),
        new HeaderSpecification(i, 'L3', 'active_l3', 'category', 'opportunity_'),
        showL4 ? new HeaderSpecification(i, 'L4', 'active_l4', 'category', 'opportunity_') : undefined,
        new HeaderSpecification(i, 'Opportunity Rank', 'opportunity_rank', '2_decimal', 'opportunity_', undefined,
            <OpportunityRankPopoverText/>),
        new HeaderSpecification(i, 'Spend', 'spend', 'currency', 'opportunity_'),
    ] as (undefined | HeaderSpecification)[]).filter(v => v !== undefined) as HeaderSpecification[];
}

export function SupBreakdownHeaders(i: BagAdvancedInsightsManager, p: ProfileStore, showL4: boolean, hideParents: boolean, synergyStore: SynergyStore) {
    const hideSuppliers = i.groupParentSuppliersFilter;

    return ([
        hideParents ? null : new HeaderSpecification(i, p.p.parentSupplierAltName ?? 'Parent Supplier', 'sp_name', 'string', 'sup_breakdown_'),
        hideSuppliers ? null : new HeaderSpecification(i, 'Supplier Name', 's_name', 'string', 'sup_breakdown_'),
        (!p.p.showSupplierIdInAdvDashboard || hideSuppliers) ? null : new HeaderSpecification(i, 'Supplier ID', 's_id', 'string', 'sup_breakdown_'),
        hideSuppliers ? null : new HeaderSpecification(i, 'Supplier Country', 's_country_code', 'string', 'sup_breakdown_'),
        p.p.synergyDashboardShowBusinessUnits ? new HeaderSpecification(i, p.buDataName, 'business_units', 'string[]', 'sup_breakdown_', true) : null,
        p.p.synergyDashboardShowCDescriptionFilter ? new HeaderSpecification(i, p.cDescriptionDataName, 'c_description', 'string', 'sup_breakdown_') : null,
        // p.p.synergyDashboardShowCAddressFilter ? new HeaderSpecification(i, 'Region', 'c_address', 'string', 'sup_breakdown_') : null,
        new HeaderSpecification(i, 'L1', 'active_l1', 'category', 'sup_breakdown_'),
        new HeaderSpecification(i, 'L2', 'active_l2', 'category', 'sup_breakdown_'),
        new HeaderSpecification(i, 'L3', 'active_l3', 'category', 'sup_breakdown_'),
        showL4 ? new HeaderSpecification(i, 'L4', 'active_l4', 'category', 'sup_breakdown_') : null,
        (synergyStore.isSynergyDatabag && p.opportunitySplitAllowed) ? new HeaderSpecification(i, 'Source', 'p_src_databag_id', 'string', 'opportunity_') : null,
        p.p.synergyDashboardShowDatabagName ? new HeaderSpecification(i, 'Dataset Name', 'databag_name', 'string', 'sup_breakdown_') : null,
        new HeaderSpecification<AdvancedSupplierBreakdownData>(i, '', 'context_menu_action', 'action', '', true, undefined, data =>
            <ContextMenuAction data={data}/>),
        new HeaderSpecification(i, synergyStore.isSynergyDatabag ? 'Supplier Category Spend' : 'Total spend', 'total_spend', 'currency', 'sup_breakdown_'),
        synergyStore.isSynergyDatabag ? new HeaderSpecification(i, 'Total Spend', 'parent_spend', 'currency', 'sup_breakdown_') : null,
    ] as (null | HeaderSpecification)[]).filter(v => v !== null) as HeaderSpecification[]
}

export function excelColumns(i: BagAdvancedInsightsManager, p: ProfileStore, showL4: boolean, hideParents: boolean, synergyStore: SynergyStore): ExcelDataColumns[] {
    const headers = SupBreakdownHeaders(i, p, showL4, hideParents, synergyStore);
    const excelColumns: ExcelDataColumns[] = headers
        .filter(h =>
            h.key !== 'parent_spend'
            && h.key !== 'p_src_databag_id'
            && h.key !== 'context_menu_action'
        )
        .map(h => ({field: h.key, title: h.label}))

    // Include the download of all address information
    excelColumns.push(
        {model: 'data_source.supplier', field: 's_address', title: 'Supplier Address'},
        {model: 'data_source.supplier', field: 's_postal', title: 'Supplier Postal Code'},
        {model: 'data_source.supplier', field: 's_city', title: 'Supplier City'},
        {model: 'data_source.supplier', field: 's_country', title: 'Supplier Country'},
        {model: 'data_source.supplier', field: 's_region', title: 'Supplier Region'},
    )

    return excelColumns;
}

export function BuPivotHeaders(i: BagAdvancedInsightsManager, p: ProfileStore, showL4: boolean, hideParents: boolean, synergyStore: SynergyStore) {
    const hideSuppliers = i.groupParentSuppliersFilter;

    const cols: (null | HeaderSpecification)[] = ([
        hideParents ? null : new HeaderSpecification(i, p.p.parentSupplierAltName ?? 'Parent Supplier', 'sp_name', 'string', 'bu_pivot_', true),
        hideSuppliers ? null : new HeaderSpecification(i, 'Supplier Name', 's_name', 'string', 'bu_pivot_', true),
        // (!p.p.showSupplierIdInAdvDashboard || hideSuppliers) ? null : new HeaderSpecification(i, 'Supplier ID', 's_id', 'string', 'bu_pivot_'),
        // hideSuppliers ? null : new HeaderSpecification(i, 'Supplier Country', 's_country_code', 'string', 'bu_pivot_'),
        // p.p.synergyDashboardShowCDescriptionFilter ? new HeaderSpecification(i, p.cDescriptionDataName, 'c_description', 'string', 'bu_pivot_') : null,
        // p.p.synergyDashboardShowCAddressFilter ? new HeaderSpecification(i, 'Region', 'c_address', 'string', 'bu_pivot_') : null,
        new HeaderSpecification(i, 'L1', 'active_l1', 'category', 'bu_pivot_', true),
        new HeaderSpecification(i, 'L2', 'active_l2', 'category', 'bu_pivot_', true),
        new HeaderSpecification(i, 'L3', 'active_l3', 'category', 'bu_pivot_', true),
        showL4 ? new HeaderSpecification(i, 'L4', 'active_l4', 'category', 'bu_pivot_', true) : null,
        // (synergyStore.isSynergyDatabag && p.opportunitySplitAllowed) ? new HeaderSpecification(i, 'Source', 'p_src_databag_id', 'string', 'opportunity_') : null,
        // p.p.synergyDashboardShowDatabagName ? new HeaderSpecification(i, 'Dataset Name', 'databag_name', 'string', 'bu_pivot_') : null,
        // hideSuppliers ? null : new HeaderSpecification<AdvancedSupplierBreakdownData>(i, '', 'context_menu_action', 'action', '', true, undefined, data =>
        //     <ContextMenuAction data={data}/>),
        // new HeaderSpecification(i, synergyStore.isSynergyDatabag ? 'Supplier Category Spend' : 'Total spend', 'total_spend', 'currency', 'bu_pivot_'),
        // synergyStore.isSynergyDatabag ? new HeaderSpecification(i, 'Total Spend', 'parent_spend', 'currency', 'bu_pivot_') : null,
    ])
    const pivotCols = (i.advancedBusinessUnitPivotColumns ?? []).map(bu =>
        new HeaderSpecification(i, bu.bu_name, `spend_bu_${bu.id}`, 'currency', 'bu_pivot_', undefined, undefined, undefined, 'bu')
    )

    return [...cols, ...pivotCols].filter(v => v !== null) as HeaderSpecification[];
}

export function buPivotExcelColumns(i: BagAdvancedInsightsManager, p: ProfileStore, showL4: boolean, hideParents: boolean, synergyStore: SynergyStore): ExcelDataColumns[] {
    const headers = BuPivotHeaders(i, p, showL4, hideParents, synergyStore);
    const excelColumns: ExcelDataColumns[] = headers
        .filter(h => h.key !== 'parent_spend' && h.key !== 'p_src_databag_id')
        .map(h => ({field: h.key, title: h.label}))

    const supplierName = headers.findIndex(h => h.key === 's_name');
    const insertPosition = supplierName === -1 ? headers.length : supplierName + 1;

    // Include the download of all address information
    excelColumns.splice(insertPosition, 0,
        {model: 'data_source.supplier', field: 's_address', title: 'Supplier Address'},
        {model: 'data_source.supplier', field: 's_postal', title: 'Supplier Postal Code'},
        {model: 'data_source.supplier', field: 's_city', title: 'Supplier City'},
        {model: 'data_source.supplier', field: 's_country', title: 'Supplier Country'},
        {model: 'data_source.supplier', field: 's_region', title: 'Supplier Region'},
    )

    return excelColumns;
}
